import React from 'react'
import * as ROUTES from 'constants/routes'
import { Button, Flex, Icon, Text, Link } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { ExternalLinkIcon } from 'lucide-react'
import { formatInTimeZone } from 'date-fns-tz'
import { VndlyCandidateIssuesItem } from 'api/admin/vndly/listVndlyCandidateIssues'
import { useVndlyCandidateIssuesContextValue } from '../context/VndlyCandidateIssuesProvider'
import { useVndlyIssuesContextValue } from 'pages/VndlyIssues/context/VndlyIssuesProvider'
import { CreateVndlyCandidateButton } from './CreateVndlyCandidateButton'
import { LinkVndlyCandidateButton } from './LinkVndlyCandidateButton'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const VNDLY_SHIFTS_DEFAULT_TIMEZONE = 'America/New_York' as const

function buildVndlyCandidatesSearchUrl(
  instanceUrl: string,
  workerName: string
): string {
  const baseUrl = instanceUrl.replace(/\/$/, '')
  const params = new URLSearchParams({
    search: workerName,
  })

  return `${baseUrl}/candidates?${params.toString()}`
}

const SearchVndlyCandidatesButton = ({
  instanceId,
  workerName,
}: {
  instanceId: number
  workerName: string
}) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instanceUrl = instances.find(
    (instance) => instance.id === instanceId
  )?.instanceUrl

  if (instanceUrl === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildVndlyCandidatesSearchUrl(instanceUrl, workerName)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Search in VNDLY</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const InstanceCell = ({ instanceId }: { instanceId: number }) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instance = instances.find((instance) => instance.id === instanceId)

  return <Text>{instance?.instanceUrl}</Text>
}

const CompanyCell = ({
  instanceId,
  companyId,
}: {
  instanceId: number
  companyId: number
}) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const company = instances
    .find((instance) => instance.id === instanceId)
    ?.companies.find((company) => company.id === companyId)

  if (!company) {
    return <Text>(unknown)</Text>
  }

  return (
    <Text>
      {company.name} (
      <Link
        to={ROUTES.COMPANY_DETAIL.replace(':companyId', company.id.toString())}
        target="_blank"
      >
        {company.id}
      </Link>
      )
    </Text>
  )
}

const columns: ColumnDef<VndlyCandidateIssuesItem>[] = [
  {
    header: 'Instance',
    accessorKey: 'instanceId',
    cell: ({ row }) => <InstanceCell instanceId={row.original.instanceId} />,
  },
  {
    header: 'Company',
    accessorKey: 'companyId',
    cell: ({ row }) => (
      <CompanyCell
        instanceId={row.original.instanceId}
        companyId={row.original.companyId}
      />
    ),
  },
  {
    header: 'Worker',
    accessorKey: 'workerId',
    cell: ({ row }) => (
      <Text>
        {row.original.workerName} (
        <Link
          to={ROUTES.USERS.replace(
            ':keyword',
            row.original.workerId.toString()
          )}
          target="_blank"
        >
          {row.original.workerId}
        </Link>
        )
      </Text>
    ),
  },
  {
    header: 'Birth MMDD',
    accessorKey: 'birthMmdd',
    cell: ({ row }) => <Text>{row.original.birthMmdd ?? '-'}</Text>,
  },
  {
    header: 'SSN Last 4',
    accessorKey: 'ssnLast4',
    cell: ({ row }) => <Text>{row.original.ssnLast4 ?? '-'}</Text>,
  },
  {
    header: 'Shift Starts At',
    accessorKey: 'shiftStartsAt',
    cell: ({ row }) => {
      const shiftStartsAt = formatInTimeZone(
        row.original.shiftStartsAt,
        VNDLY_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
      return <Text>{shiftStartsAt}</Text>
    },
  },
  {
    header: 'Issues',
    cell: ({ row }) => {
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {row.original.issues.join(', ')}
        </Text>
      )
    },
  },
  {
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <Flex gap={2} flexWrap="wrap">
          <SearchVndlyCandidatesButton
            instanceId={row.original.instanceId}
            workerName={row.original.workerName}
          />
          <CreateVndlyCandidateButton
            workerId={row.original.workerId}
            workerName={row.original.workerName}
            vndlyInstanceId={row.original.instanceId}
          />
          <LinkVndlyCandidateButton
            workerId={row.original.workerId}
            workerName={row.original.workerName}
            vndlyInstanceId={row.original.instanceId}
          />
        </Flex>
      )
    },
  },
]

export const VndlyCandidateIssuesMain = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useVndlyCandidateIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No candidate issues found."
      emptyStateHeight={50}
    />
  )
}
